
import React from 'react';
import { useControlVisibility } from '@/hooks/useControlVisibility';
import { useControlStateManager } from '@/hooks/useControlStateManager';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import { useAudioSessionTracking } from '@/hooks/useAudioSessionTracking';
import { ActionButtonsVerticalProps } from './ActionButtonsVerticalProps';
import FavoriteButton from './FavoriteButton';
import CommentButton from './CommentButton';
import ActionDropdownMenu from './ActionDropdownMenu';
import PrayerActionButton from './PrayerActionButton';
import AudioControlButton from './AudioControlButton';
import { LAYERS } from '@/utils/layers';

interface ActionButtonsVerticalContainerProps extends ActionButtonsVerticalProps {}

export default function ActionButtonsVerticalContainer({
  prayer,
  interactions,
  onFavorite,
  onComment,
  onShare,
  onBookmark,
  onMore,
  onPrayerStatus,
  onEdit,
  onReport,
  onDelete,
  positioning = 'fixed',
  isBookmarked,
  hasCTAButton = false,
  isAudioExpanded = false,
  onPray
}: ActionButtonsVerticalContainerProps) {
  const controlState = useControlStateManager();
  const { isPlaying, currentSessionId, controlsVisible } = useAbielAudio();
  const { hasPlayedAudio, isPausedAudio } = useAudioSessionTracking();
  
  // Use centralized control visibility system with audio expansion state
  const {
    shouldShowPrayerButton,
    shouldShowFloatingActions,
    containerClasses,
    transitionClasses
  } = useControlVisibility({
    positioning,
    hasCTAButton,
    prayerId: prayer.id,
    scrollY: controlState.scrollY,
    isExpanded: isAudioExpanded
  });

  // Enhanced logic: Show audio button when audio is playing but controls are hidden
  const isCurrentlyPlaying = isPlaying && currentSessionId === prayer.id;
  const hasBeenPlayed = hasPlayedAudio(prayer.id);
  const isPaused = isPausedAudio(prayer.id);
  
  // Audio button shows when actively playing AND controls are hidden (floating state)
  const shouldShowAudioButton = isCurrentlyPlaying && !controlsVisible;
  
  // Prayer button shows when:
  // 1. shouldShowPrayerButton is true AND
  // 2. Audio floating button should NOT be visible AND
  // 3. Either never played OR is currently paused (but not playing)
  const finalShouldShowPrayerButton = shouldShowPrayerButton && 
    !shouldShowAudioButton && 
    (!hasBeenPlayed || isPaused || !isCurrentlyPlaying);

  console.log('[ActionButtonsVertical] 🔄 ENHANCED BUTTON VISIBILITY DEBUG:', {
    prayerId: prayer.id,
    isPlaying,
    currentSessionId,
    isCurrentlyPlaying,
    controlsVisible,
    hasBeenPlayed,
    isPaused,
    shouldShowAudioButton: `${shouldShowAudioButton} (${isCurrentlyPlaying} && !${controlsVisible})`,
    finalShouldShowPrayerButton,
    shouldShowFloatingActions,
    '🎯 FLOATING_BUTTON_SHOULD_SHOW': shouldShowAudioButton
  });

  // Clean prayer button handler - only handles prayer choice dialog
  const handlePrayClick = () => {
    console.debug('[ActionButtonsVertical] Prayer button clicked - opening prayer choice dialog');
    if (onPray) {
      onPray();
    }
  };

  // Enhanced button class for FABs with smooth transitions - Lower z-index to stay below dialogs
  const baseButton =
    `w-12 h-12 rounded-full border backdrop-blur-xl shadow-[0_6px_32px_0_rgba(244,196,48,0.15)] dark:shadow-[0_6px_48px_0_rgba(255,255,255,0.09)] ring-2 ring-gold/20 dark:ring-white/15 border-black/10 dark:border-white/20 focus-visible:ring-4 focus-visible:ring-gold/40 ${transitionClasses} flex items-center justify-center relative bg-gradient-to-br from-white/85 via-gold/10 to-gold/5 dark:from-black/80 dark:via-gold/10 dark:to-gold/0 hover:scale-110 hover:ring-gold/40 cursor-pointer !pointer-events-auto` + ` z-[${LAYERS.CONTENT_OVR}]`;
  const accentGlow = "after:content-[''] after:absolute after:inset-0 after:rounded-full after:pointer-events-none after:opacity-0 group-hover:after:opacity-70 group-active:after:opacity-90 after:bg-gold/25 dark:after:bg-gold/30 after:blur-sm after:transition-all";

  // Apply visibility and opacity based on control state - ENSURE ALWAYS CLICKABLE
  const controlStyle = {
    opacity: shouldShowFloatingActions ? Math.max(controlState.controlOpacity, 0.9) : 0,
    transform: shouldShowFloatingActions ? 'translateY(0)' : 'translateY(20px)',
    pointerEvents: 'auto' // Always allow pointer events
  } as React.CSSProperties;

  return (
    <div className={containerClasses} style={controlStyle}>
      {/* 
        FLOATING ACTION BAR BUTTON ORDER:
        
        Due to flex-col-reverse in containerClasses, the visual order is REVERSED from JSX order:
        
        JSX Order (first to last):              Visual Order (bottom to top):
        1. ActionDropdownMenu              →    6. ActionDropdownMenu (bottom)
        2. CommentButton                   →    5. CommentButton  
        3. FavoriteButton                  →    4. FavoriteButton
        4. AudioControlButton              →    3. AudioControlButton
        5. PrayerActionButton              →    2. PrayerActionButton
        
        This design ensures the action bar grows UPWARD from its fixed bottom position,
        maintaining a stable anchor point and preventing content displacement.
        
        ENHANCED LOGIC: Audio button shows when actively playing AND controls are hidden.
        This creates a floating play/pause button after the 10-second auto-hide timer.
        When audio is paused or stopped, prayer emoji button appears to allow prayer choice dialog.
      */}

      {/* More Options - Appears at visual BOTTOM */}
      <ActionDropdownMenu
        prayer={prayer}
        onShare={onShare}
        onBookmark={onBookmark}
        onPrayerStatus={onPrayerStatus}
        onEdit={onEdit}
        onReport={onReport}
        onDelete={onDelete}
        isBookmarked={isBookmarked}
        baseButton={baseButton}
        accentGlow={accentGlow}
      />

      {/* Comments Button */}
      <CommentButton
        onComment={onComment}
        commentCount={interactions.commentCount}
        baseButton={baseButton}
        accentGlow={accentGlow}
      />

      {/* Favorite Button */}
      <FavoriteButton
        onFavorite={onFavorite}
        isFavorite={interactions.isFavorite}
        baseButton={baseButton}
        accentGlow={accentGlow}
      />

      {/* Audio Control Button - Shows when actively playing AND controls are hidden (floating state) */}
      {shouldShowAudioButton && (
        <AudioControlButton
          prayer={prayer}
          baseButton={baseButton}
          accentGlow={accentGlow}
        />
      )}

      {/* Prayer Action Button - Shows when not playing (including paused state) */}
      {finalShouldShowPrayerButton && (
        <PrayerActionButton
          onPray={handlePrayClick}
          baseButton={baseButton}
          accentGlow={accentGlow}
        />
      )}
    </div>
  );
}
